<template>
  <div
    v-if="cookieManagementVisible"
    class="cookie-tip"
  >
    <div class="cookie-tip__container">
      <div class="cookie-tip__text">
        {{ $t('cookie-management-tip-title') }}
      </div>
      <div class="cookie-tip__button">
        <div
          class="button"
          @click="manageCookie"
        >
          {{ $t('cookie-management-button-manage') }}
        </div>
        <ElButton
          type="primary"
          @click="saveAndAgree(true, 'accept')"
        >
          {{ $t('cookie-management-tip-button-accept') }}
        </ElButton>
      </div>
    </div>
  </div>
  <ElDialog
    v-model="visible"
    :show-close="false"
    :width="640"
    append-to-body
    class="cookie-dialog"
  >
    <div class="cookie-dialog__header">
      <h5 class="cookie-dialog__title">
        {{ $t('cookie-management-dialog-title') }}
      </h5>
      <div class="close">
        <a
          class="pp-link-button"
          @click="onClose"
        >
          <CloseIcon />
        </a>
        <a
          class="h5-close"
          @click="onClose"
          >{{ $t('close') }}</a
        >
      </div>
    </div>

    <div class="cookie-dialog__text">
      {{ $t('cookie-management-dialog-desc') }}
    </div>
    <div class="cookie-dialog__content">
      <Collapse :title="$t('cookie-management-dialog-option-necessary')">
        <template #button>
          <span>{{ $t('always-enabled') }}</span>
        </template>
        <template #description>
          <div class="cookie-dialog__desc">
            {{ $t('cookie-management-dialog-option-necessary-desc') }}
            <a @click="openPrivacyPage('https://www.paymentwall.com/privacypolicy')">
              {{ $t('paypal-privacy-policy', ['PaymentWall']) }}</a
            >,&nbsp;<a @click="openPrivacyPage('https://www.paypal.com/c2/webapps/mpp/ua/cookie-full')">{{
              $t('paypal-privacy-policy', ['PayPal'])
            }}</a>
          </div>
        </template>
      </Collapse>
      <Collapse :title="$t('cookie-management-dialog-option-optional')">
        <template #button>
          <ElSwitch
            v-model="allowAnalysis"
            :width="34"
            active-color="#306EFF"
          />
        </template>
        <template #description>
          <div class="cookie-dialog__desc">
            {{ $t('cookie-management-dialog-option-optional-desc') }}
            <a @click="openPrivacyPage('https://policies.google.com/technologies/cookies')">{{
              $t('privacy-policy')
            }}</a
            >,&nbsp;Microsoft Clarity:
            <a @click="openPrivacyPage('https://privacy.microsoft.com/privacystatement')">{{ $t('privacy-policy') }}</a>
          </div>
        </template>
      </Collapse>
    </div>

    <template #footer>
      <ElButton
        type="primary"
        @click="saveAndAgree(allowAnalysis, 'manage')"
      >
        {{ $t('save-and-agree') }}
      </ElButton>
    </template>
  </ElDialog>
</template>

<script lang="ts" setup>
import { ElDialog } from 'element-plus'
import { onMounted, ref, watch } from 'vue'
import { whenever } from '@vueuse/core'
import { StorageKey } from '~/types/enum'
import $track from '~/track/webYunpanPublicModule'
import { getLevel2domain } from '~/utils'
import Collapse from '@/components/Collapse.vue'
import CloseIcon from '@/components/icons/CloseIcon.vue'
import { useCookie } from '#imports'

const cookieManagementVisible = ref(!useCookie(StorageKey.ALLOW_ANALYSIS).value) // 判断是否展示Cookie管理
const visible = ref(false) // 弹窗是否展示
const allowAnalysis = ref(true) // 允许第三方分析按钮

/* 保存并关闭 */
const saveAndAgree = (value: boolean, type: string) => {
  useCookie(StorageKey.ALLOW_ANALYSIS, {
    domain: getLevel2domain(),
    maxAge: 365 * 24 * 60 * 60,
  }).value = `${value}`
  visible.value = false
  cookieManagementVisible.value = false
  if (type === 'accept') {
    $track.web_yunpan_cookie_tips_click({ current_address: window.location.origin, button: 'accept' })
  } else if (type === 'manage') {
    $track.web_yunpan_cookie_pop_click({ current_address: window.location.origin, button: 'save' })
  }
}

/* 打开管理页 */
const manageCookie = () => {
  visible.value = true
  $track.web_yunpan_cookie_tips_click({ current_address: window.location.origin, button: 'manage' })
}

/* 关闭弹窗 */
const onClose = () => {
  visible.value = false
  $track.web_yunpan_cookie_pop_click({ current_address: window.location.origin, button: 'close' })
}

/* 打开隐私政策页 */
const openPrivacyPage = (url: string) => {
  window.open(url)
  $track.web_yunpan_cookie_pop_click({ current_address: window.location.origin, button: 'privacy_policy' })
}

onMounted(async () => {
  if (cookieManagementVisible.value) {
    $track.web_yunpan_cookie_tips_show({
      current_address: window.location.origin,
    })
  }
})

watch(allowAnalysis, value => {
  $track.web_yunpan_cookie_pop_click({
    current_address: window.location.origin,
    button: value ? 'open_third_analysis' : 'close_third_analysis',
  })
})

whenever(cookieManagementVisible, () => {
  $track.web_yunpan_cookie_tips_show({
    current_address: window.location.origin,
  })
})
</script>

<style lang="less" scoped>
.cookie-tip {
  //position: fixed;
  //z-index: 999;
  //bottom: 0;
  //left: 0;

  width: 100%;

  background: #fff;

  &__container {
    display: flex;
    align-items: flex-end;
    justify-content: center;
    flex-direction: column;
    gap: 24px;

    margin: 0 auto;
    padding: 32px 42px;

    //box-shadow: 0 -1px 0 rgba(0, 0, 0, 0.1);
    border-top: 1px solid rgba(0, 0, 0, 0.1);

    @media screen and (max-width: 640px) {
      flex-direction: column;

      height: auto;
      margin-right: 0;
      padding: 24px 13px 16px;
    }
  }

  &__text {
    width: 100%;
    font-size: 14px;
    line-height: 21px;

    @media screen and (max-width: 640px) {
      margin: 0 0 13px 0;
    }
  }

  &__button {
    display: flex;
    justify-content: space-around;
    gap: 24px;

    .button {
      display: flex;
      align-items: center;
      color: #666;
      line-height: 18px;
      font-size: 14px;
      cursor: pointer;
      min-height: 36px;
    }

    .el-button {
      background-color: var(--color-primary);
    }

    //.el-button:first-child {
    //  border: none;
    //}
  }
}

.cookie-dialog {
  padding: 32px;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;

    font-weight: 600;

    @media screen and (max-width: 640px) {
      justify-content: center;
    }
  }

  &__title {
    color: #000;

    font-size: 18px;
    font-weight: 600;
    line-height: 27px;

    @media screen and (max-width: 640px) {
      margin-top: 14%;

      text-align: center;
    }
  }

  &__content {
    margin-top: 24px;
  }

  &__text {
    margin-top: 8px;

    font-size: 14px;
    line-height: 22px;

    @media screen and (max-width: 640px) {
      text-align: center;
    }
  }

  &__desc {
    margin: 10px 0 6px 36px;

    font-size: 13px;
    line-height: 20px;
  }

  .el-button {
    @media screen and (max-width: 640px) {
      width: 100%;
      height: 48px !important;

      font-size: 17px;
    }
  }
}
</style>
<style lang="less">
.cookie-dialog {
  overflow: hidden;

  .el-overlay-dialog {
    @media screen and (max-width: 640px) {
      background-color: #fff;
    }
  }

  @media screen and (max-width: 640px) {
    --el-dialog-border-radius: 0;
    overflow: visible;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    width: 100%;
    height: auto;
    min-height: 100%;
    top: 0 !important;
    transform: translateY(0px) !important;

    .pp-link-button {
      display: none;
    }
  }

  .el-dialog {
    &__header {
      display: none !important;
    }

    &__headerbtn {
      display: none !important;
    }

    &__body {
      padding: 32px !important;
      word-break: break-word;
      @media screen and (max-width: 640px) {
        height: auto;
        padding: 16px !important;
      }
    }

    &__footer {
      padding: 16px 32px !important;
      //box-shadow: 0px -1px 0px rgba(0, 0, 0, 0.1);
      .el-button {
        background-color: var(--color-primary);
      }

      @media screen and (max-width: 640px) {
        padding: 16px !important;
      }
    }
  }
}

.h5-close {
  display: none;

  @media screen and (max-width: 640px) {
    position: absolute;
    top: 21px;
    right: 16px;

    display: block;

    text-decoration: none;

    font-size: 14px;
  }
}
</style>
